import { formatRegionLocation } from '../utils/regionLocation';
import RegionLocation from './RegionLocation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  DeleteOutline as DeleteOutlineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { ProviderError } from '../types/providers';
import { useAdaptivePolling } from '../hooks/useAdaptivePolling';

// Map a provider error_code to a chip colour. Quota/capacity issues are warnings (self-healing);
// auth/permission issues are hard errors that need operator attention.
const errorCodeColor = (code: string | null): 'error' | 'warning' | 'default' => {
  if (!code) return 'default';
  const c = code.toLowerCase();
  if (c.includes('permission') || c.includes('auth') || c.includes('denied') || c.includes('credential')) {
    return 'error';
  }
  if (c.includes('quota') || c.includes('exhausted') || c.includes('ratelimit') || c.includes('capacity')) {
    return 'warning';
  }
  return 'default';
};

// The backend serializes naive UTC timestamps (datetime.utcnow().isoformat()) with no timezone
// suffix. Append 'Z' when one is missing so the browser parses them as UTC rather than local time.
const parseUtcMs = (iso: string | null): number => {
  if (!iso) return NaN;
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`).getTime();
};

const timeAgo = (iso: string | null): string => {
  const then = parseUtcMs(iso);
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Absolute time rendered explicitly in UTC (and labelled as such) to match the stored value.
const formatAbsoluteUtc = (iso: string | null): string => {
  const t = parseUtcMs(iso);
  if (Number.isNaN(t)) return '—';
  return `${new Date(t).toLocaleString('en-US', { timeZone: 'UTC', hour12: false })} UTC`;
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const ErrorDetail: React.FC<{ error: ProviderError }> = ({ error }) => (
  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
    <Typography
      variant="body2"
      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1.5, fontFamily: 'monospace' }}
    >
      {error.error_message}
    </Typography>
    <DetailRow label="Error code" value={error.error_code || '—'} />
    <DetailRow label="Action" value={error.action} />
    <DetailRow label="Region" value={formatRegionLocation(error.region_name || error.region_id)} />
    <DetailRow label="Occurrences" value={error.occurrence_count} />
    <DetailRow label="First seen" value={formatAbsoluteUtc(error.first_seen_at)} />
    <DetailRow label="Last seen" value={formatAbsoluteUtc(error.last_seen_at)} />
    <DetailRow label="Status" value={error.status} />
    {error.details && Object.keys(error.details).length > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Details
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.5,
            p: 1,
            fontSize: 12,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflowX: 'auto',
          }}
        >
          {JSON.stringify(error.details, null, 2)}
        </Box>
      </Box>
    )}
  </Box>
);

interface ProviderErrorsPanelProps {
  providerId: string;
}

/**
 * Cloud-provider error history for a single provider, shown at the bottom of the Region Management
 * section and scoped to the selected provider. Errors can be selected and deleted, or expanded to
 * read the full message and structured details.
 */
const ProviderErrorsPanel: React.FC<ProviderErrorsPanelProps> = ({ providerId }) => {
  const [errors, setErrors] = useState<ProviderError[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const intervalMs = useAdaptivePolling({ activeMs: 20_000, idleMs: 60_000 });

  const load = useCallback(
    async (showSpinner = false) => {
      if (!providerId) {
        setErrors([]);
        return;
      }
      if (showSpinner) setLoading(true);
      try {
        // 'all' → full history (active + acknowledged), most recent first.
        const data = await api.getProviderErrors(providerId, 'all', 200);
        setErrors(data || []);
        // Drop selections/expansions for rows that no longer exist.
        const ids = new Set((data || []).map((e) => e.id));
        setSelected((prev) => new Set(Array.from(prev).filter((id) => ids.has(id))));
        setExpanded((prev) => new Set(Array.from(prev).filter((id) => ids.has(id))));
      } catch (e) {
        console.error('Failed to load provider errors:', e);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [providerId]
  );

  useEffect(() => {
    setSelected(new Set());
    setExpanded(new Set());
    load(true);
  }, [load]);

  useEffect(() => {
    if (!providerId) return undefined;
    const id = window.setInterval(() => load(false), intervalMs);
    return () => window.clearInterval(id);
  }, [load, intervalMs, providerId]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = errors.length > 0 && selected.size === errors.length;
  const someSelected = selected.size > 0 && selected.size < errors.length;

  const handleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(errors.map((e) => e.id)));
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      await api.deleteProviderErrors(providerId, Array.from(selected));
      setSelected(new Set());
      await load(false);
    } catch (e) {
      console.error('Failed to delete provider errors:', e);
    } finally {
      setDeleting(false);
    }
  };

  const totalOccurrences = useMemo(
    () => errors.reduce((sum, e) => sum + (e.occurrence_count || 1), 0),
    [errors]
  );

  return (
    <Paper variant="outlined">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ErrorOutlineIcon color={errors.length > 0 ? 'error' : 'disabled'} fontSize="small" />
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Provider Errors
        </Typography>
        {errors.length > 0 && (
          <Chip
            label={`${errors.length}${totalOccurrences > errors.length ? ` · ${totalOccurrences} occ.` : ''}`}
            color="error"
            size="small"
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={selected.size === 0 ? 'Select one or more errors first to delete' : ''}>
          {/* span wrapper so the tooltip still shows while the button is disabled */}
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
              onClick={handleDeleteSelected}
              disabled={deleting || selected.size === 0}
            >
              {selected.size > 0 ? `Delete ${selected.size} selected` : 'Delete selected'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => load(true)} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && errors.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : errors.length === 0 ? (
        <Box sx={{ px: 2, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No errors recorded for this provider.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Error</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Action</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell>Last seen (UTC)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors.map((err) => {
                const isExpanded = expanded.has(err.id);
                return (
                  <React.Fragment key={err.id}>
                    <TableRow hover selected={selected.has(err.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(err.id)}
                          onChange={() => toggleSelect(err.id)}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 40 }}>
                        <IconButton size="small" onClick={() => toggleExpand(err.id)}>
                          {isExpanded ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {err.error_code && (
                            <Chip
                              label={err.error_code}
                              color={errorCodeColor(err.error_code)}
                              size="small"
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {err.error_message}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          <RegionLocation region={err.region_name || err.region_id} compact />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {err.action}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {err.occurrence_count > 1 ? `×${err.occurrence_count}` : '1'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatAbsoluteUtc(err.last_seen_at)}>
                          <Typography variant="body2" color="text.secondary">
                            {timeAgo(err.last_seen_at)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, border: 0 }} colSpan={7}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5 }}>
                            <ErrorDetail error={err} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ProviderErrorsPanel;
